import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import CmsPopup from '../../../../components/CmsPopup';
import { AppTextField } from '../../../../customComponents/AppTextField';
import { AppNumberTextField } from '../../../../customComponents/AppNumberTextField';
import { AppDropdownField, DropdownOption } from '../../../../customComponents/AppDropdownField';
import { AppSearchDropdown, SearchCardItem } from '../../../../customComponents/AppSearchDropdown';
import CustomTiptapEditor from '../../../../components/Editor/CustomTiptapEditor';
import { CmsPopupBtnType } from '../../../../components/CmsPopupBtn';
import MultiImageAdd from '../../../../components/MultiImageAdd';
import { getAuctioneerListCompact } from '../../../../lib/api/admin/adminApi';
import { AuctioneerCompactData } from '../../../../lib/api/admin/adminApi.types';
import { uploadCommonFile, uploadEditorImages, processEditorContent } from '../../../../lib/api/common/commonApi';
import { devLog , devError} from '../../../../utils/devLogger';

// 서버 파일명을 실제 이미지 URL로 변환하는 함수
const getImageSrc = (imageUrl?: string | null) => {
  if (!imageUrl || imageUrl.trim() === "") {
    return "";
  }
  
  // 이미 완전한 URL인 경우 (http, https로 시작하거나 blob: URL)
  if (imageUrl.startsWith("http") || imageUrl.startsWith("blob:")) {
    return imageUrl;
  }
  
  // 절대 경로인 경우 (/로 시작)
  if (imageUrl.startsWith("/")) {
    return `${process.env.REACT_APP_API_HOST}${imageUrl}`;
  }
  
  // 단순 파일명인 경우 (서버에서 받은 파일명)
  return `${process.env.REACT_APP_API_HOST}/${imageUrl}`;
};

// LOT 폼 데이터 타입
export interface LotFormData {
  auctioneerName: string;
  auctioneerId: string;
  majorCategory: string;
  minorCategory: string;
  artworkName: string;
  startPriceKRW: number;
  thumbnailImages: string[];
  artworkDetail: string;
  disabled: boolean;
}

interface LotFormPopupProps {
  isOpen: boolean;
  isEditMode: boolean;
  isSaving?: boolean;
  formData: LotFormData;
  formErrors: Partial<Record<keyof LotFormData, string>>;
  onClose: () => void;
  onSave: (processedFormData?: Partial<LotFormData>) => Promise<void> | void; // 처리된 데이터를 전달할 수 있도록 수정
  onDelete?: () => void;
  onInputChange: (field: keyof LotFormData, value: string | number | string[] | boolean) => void;
  onImageUpload: (files: File[]) => void;
  readonlyAuctioneer?: boolean; // 경매사 필드를 readonly로 만들지 여부
}

// 대분류 카테고리 옵션
const MAJOR_CATEGORY_OPTIONS: DropdownOption[] = [
  { value: 'AR', label: '예술품' },
  { value: 'AN', label: '골동품 & 장식예술' },
  { value: 'CG', label: '세라믹 & 글라스' },
  { value: 'JW', label: '주얼리 & 시계' },
  { value: 'FL', label: '패션 & 명품' },
  { value: 'CO', label: '수집품' },
  { value: 'CB', label: '동전, 지폐, 우표' },
  { value: 'BM', label: '서적 & 필사본' },
  { value: 'MW', label: '군사품 & 무기' },
  { value: 'MI', label: '음악 & 악기' },
  { value: 'WS', label: '와인 & 주류' },
];

// 소분류 카테고리 옵션 (대분류에 따라 변경)
const MINOR_CATEGORY_OPTIONS: Record<string, DropdownOption[]> = {
  AR: [
    { value: 'PT', label: '회화' },
    { value: 'PM', label: '판화' },
    { value: 'DW', label: '드로잉 & 수채화' },
    { value: 'SC', label: '조각' },
    { value: 'PH', label: '사진' },
    { value: 'AA', label: '동양화' },
  ],
  AN: [
    { value: 'EF', label: '유럽 앤틱 가구' },
    { value: 'LI', label: '조명 (샹들리에, 램프 등)' },
    { value: 'CL', label: '벽시계 & 탁상시계' },
    { value: 'SV', label: '실버웨어' },
    { value: 'DA', label: '인형 & 오토마타' },
    { value: 'MP', label: '거울 & 패널' },
  ],
  CG: [
    { value: 'EP', label: '유럽 도자기 (마이센, 세브르 등)' },
    { value: 'AC', label: '동양 도자기 (고려·조선, 청·명 등)' },
    { value: 'AG', label: '아트 글라스 (무라노, 라리크 등)' },
    { value: 'TW', label: '테이블웨어' },
  ],
  JW: [
    { value: 'VJ', label: '빈티지 & 앤틱 주얼리' },
    { value: 'MJ', label: '현대 디자이너 주얼리' },
    { value: 'LW', label: '명품 시계 (롤렉스, 까르띠에 등)' },
    { value: 'AX', label: '장신구 및 액세서리' },
  ],
  FL: [
    { value: 'VC', label: '빈티지 의류' },
    { value: 'BC', label: '가방 & 클러치' },
    { value: 'SA', label: '신발 & 액세서리' },
    { value: 'ST', label: '브랜드 스카프 & 텍스타일' },
    { value: 'LB', label: '럭셔리 브랜드 (에르메스, 샤넬 등)' },
  ],
  CO: [
    { value: 'FT', label: '피규어 & 장난감' },
    { value: 'TG', label: '트레이딩 카드 & 게임' },
    { value: 'SM', label: '담배용품 (라이타, 파이프 등)' },
    { value: 'DB', label: '인형 & 테디베어' },
    { value: 'PA', label: '포스터 & 광고물' },
  ],
  CB: [
    { value: 'NC', label: '고대 동전' },
    { value: 'WB', label: '세계 지폐' },
    { value: 'CC', label: '기념주화' },
    { value: 'SD', label: '우표 및 초일봉투' },
  ],
  BM: [
    { value: 'OB', label: '고서 및 초판본' },
    { value: 'MA', label: '지도 및 지도책' },
    { value: 'SH', label: '사인본 & 수기문서' },
    { value: 'ACG', label: '예술 관련 도록' },
  ],
  MW: [
    { value: 'UB', label: '제복 및 배지' },
    { value: 'SF', label: '검 & 총기류 (비작동 전시용 포함)' },
    { value: 'WM', label: '전쟁 기념품' },
  ],
  MI: [
    { value: 'SI2', label: '현악기 (바이올린 등)' },
    { value: 'PO', label: '피아노 & 오르간' },
    { value: 'VA', label: '빈티지 오디오' },
    { value: 'LP', label: '고전 음반 & LP' },
  ],
  WS: [
    { value: 'VW', label: '빈티지 와인' },
    { value: 'WC', label: '위스키 & 꼬냑' },
    { value: 'CH', label: '샴페인' },
  ],
};

const LotFormPopup: React.FC<LotFormPopupProps> = ({
  isOpen,
  isEditMode,
  isSaving = false,
  formData,
  formErrors,
  onClose,
  onSave,
  onDelete,
  onInputChange,
  onImageUpload,
  readonlyAuctioneer = false,
}) => {
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imageUploading, setImageUploading] = useState(false); // 이미지 업로드 중 상태
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 경매사 검색 상태
  const [auctioneerSearchTerm, setAuctioneerSearchTerm] = useState('');
  const [auctioneerSearchResults, setAuctioneerSearchResults] = useState<SearchCardItem[]>([]);
  const [auctioneerSearchLoading, setAuctioneerSearchLoading] = useState(false);

  // 소분류 카테고리 옵션 (대분류에 따라 변경)
  const [minorCategoryOptions, setMinorCategoryOptions] = useState<DropdownOption[]>([]);

  // 대분류 변경 시 소분류 옵션 업데이트
  useEffect(() => {
    if (formData.majorCategory) {
      setMinorCategoryOptions(MINOR_CATEGORY_OPTIONS[formData.majorCategory] || []);
      // 대분류가 변경되면 소분류 초기화
      if (formData.minorCategory && !MINOR_CATEGORY_OPTIONS[formData.majorCategory]?.find(option => option.value === formData.minorCategory)) {
        onInputChange('minorCategory', '');
      }
    } else {
      setMinorCategoryOptions([]);
      if (formData.minorCategory) {
        onInputChange('minorCategory', '');
      }
    }
  }, [formData.majorCategory, formData.minorCategory, onInputChange]);

  // 경매사 검색 함수
  const handleAuctioneerSearch = async (searchTerm: string) => {
    setAuctioneerSearchLoading(true);
    
    try {
      // 실제 API 호출
      const response = await getAuctioneerListCompact(searchTerm);
      devLog('경매사 검색 응답:', response);
      
      // API 응답 구조 확인: response.data.data 또는 response.data
      const auctioneerList = response.data?.data || response.data || [];
      
      if (!Array.isArray(auctioneerList)) {
        devError('경매사 데이터가 배열이 아닙니다:', auctioneerList);
        setAuctioneerSearchResults([]);
        return;
      }
      
      const searchResults: SearchCardItem[] = auctioneerList.map((auctioneer: AuctioneerCompactData) => ({
        id: auctioneer.index.toString(),
        title: auctioneer.name,
        subtitle: `경매사ID: ${auctioneer.index}`,
        data: auctioneer
      }));
      
      setAuctioneerSearchResults(searchResults);
    } catch (error) {
      devError('경매사 검색 실패:', error);
      setAuctioneerSearchResults([]);
    } finally {
      setAuctioneerSearchLoading(false);
    }
  };

  // 경매사 선택 핸들러
  const handleAuctioneerSelect = (item: any) => {
    const auctioneer = item.data as AuctioneerCompactData;
    onInputChange('auctioneerId', auctioneer.index.toString());
    onInputChange('auctioneerName', auctioneer.name);
  };

  // 경매사 선택 해제 핸들러
  const handleAuctioneerClear = () => {
    onInputChange('auctioneerId', '');
    onInputChange('auctioneerName', '');
    setAuctioneerSearchTerm('');
  };

  // 여러 이미지 파일 선택 핸들러 (한번에 처리)
  const handleImagesAdd = async (files: File[]) => {
    devLog('🖼️ LOT 여러 이미지 추가:', files.length, '장');
    
    // 업로드 중이면 중복 업로드 방지
    if (imageUploading) {
      toast.warning("이미 이미지 업로드가 진행 중입니다. 잠시 후 다시 시도해 주세요.");
      return;
    }
    
    // 각 파일 검증
    const validFiles: File[] = [];
    for (const file of files) {
      // 파일 크기 검증
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name}: 파일 크기가 5MB를 초과할 수 없습니다.`);
        continue;
      }
      
      // 파일 타입 검증
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name}: 이미지 파일만 업로드할 수 있습니다.`);
        continue;
      }
      
      validFiles.push(file);
    }
    
    if (validFiles.length === 0) {
      return;
    }
    
    // 이미지 개수 제한 확인
    const remainingSlots = 3 - formData.thumbnailImages.length;
    if (remainingSlots <= 0) {
      toast.error("최대 3장의 이미지만 업로드할 수 있습니다.");
      return;
    }
    
    const filesToUpload = validFiles.slice(0, remainingSlots);
    if (filesToUpload.length < validFiles.length) {
      toast.warning(`최대 3장까지만 업로드됩니다. ${filesToUpload.length}장이 선택되었습니다.`);
    }
    
    setImageUploading(true); // 업로드 시작
    
    // 업로드 전 상태 저장 (인덱스 계산용)
    const preUploadImageUrls = [...formData.thumbnailImages];
    const preUploadImageCount = preUploadImageUrls.length;
    const preUploadFileCount = imageFiles.length;
    
    // 미리보기 즉시 생성 (업로드와 별개로)
    const newImageUrls = filesToUpload.map(file => URL.createObjectURL(file));
    const updatedImageUrls = [...preUploadImageUrls, ...newImageUrls];
    onInputChange('thumbnailImages', updatedImageUrls);
    
    // 로컬 파일 상태 업데이트
    const newFiles = [...imageFiles, ...filesToUpload];
    setImageFiles(newFiles);
    onImageUpload(newFiles);
    
    try {
      // 여러 파일을 FormData로 감싸서 서버에 업로드
      const uploadFormData = new FormData();
      filesToUpload.forEach((file, index) => {
        uploadFormData.append('file', file); // 모든 파일을 'file' 필드로 추가
      });
      
      devLog('📤 LOT 여러 이미지 업로드 요청:', filesToUpload.length, '장');
      
      // uploadCommonFile 호출
      const uploadRes = await uploadCommonFile(uploadFormData, true);
      devLog('📤 LOT 여러 이미지 업로드 응답:', uploadRes);
      
      const res = Array.isArray(uploadRes) ? uploadRes[0] : uploadRes;
      if (res?.message === "success") {
        const uploadedData = res?.data ?? [];
        
        if (uploadedData.length > 0) {
          devLog('업로드된 파일 정보들:', uploadedData);
          
          // 업로드된 파일들의 서버 파일명으로 미리보기 URL들을 교체
          // 업로드 전 상태를 기준으로 인덱스 계산
          const updatedImageUrlsWithServerNames = [...updatedImageUrls];
          const failedRelativeIndexes: number[] = [];

          filesToUpload.forEach((file, index) => {
            const fileInfo = uploadedData[index];
            const targetIndex = preUploadImageCount + index; // 새로 추가된 이미지들의 인덱스

            if (!fileInfo) {
              failedRelativeIndexes.push(index);
              devError('LOT 이미지 업로드 실패 - 응답 데이터 없음:', { fileName: file.name, index });
              return;
            }

            const fileName = typeof fileInfo === 'string' ? fileInfo : fileInfo?.fileName || fileInfo?.index;
            
            if (fileName !== undefined && fileName !== null && targetIndex < updatedImageUrlsWithServerNames.length) {
              updatedImageUrlsWithServerNames[targetIndex] = String(fileName);
              devLog(`✅ 이미지 ${targetIndex} 인덱스: 미리보기 URL → 서버 파일명 (${fileName})`);
            } else {
              failedRelativeIndexes.push(index);
              devError('LOT 이미지 업로드 실패 - 파일명 없음:', { fileInfo, index });
            }
          });

          if (failedRelativeIndexes.length > 0) {
            const failedTargetIndexes = failedRelativeIndexes.map(relativeIndex => preUploadImageCount + relativeIndex);
            const finalImageUrls = updatedImageUrlsWithServerNames.filter((_, idx) => !failedTargetIndexes.includes(idx));

            const failedFileIndexes = failedRelativeIndexes.map(relativeIndex => preUploadFileCount + relativeIndex);
            const finalFiles = newFiles.filter((_, idx) => !failedFileIndexes.includes(idx));

            onInputChange('thumbnailImages', finalImageUrls);
            setImageFiles(finalFiles);
            onImageUpload(finalFiles);

            toast.error(`일부 이미지 업로드에 실패하여 제외되었습니다. (${failedRelativeIndexes.length}장)`);
          } else {
            onInputChange('thumbnailImages', updatedImageUrlsWithServerNames);
          }
        } else {
          toast.error("업로드된 파일 정보가 없습니다.");
          // 업로드 실패 시 추가된 이미지들 제거 (업로드 전 상태로 롤백)
          onInputChange('thumbnailImages', preUploadImageUrls);
          
          const rollbackFiles = imageFiles.slice(0, imageFiles.length - filesToUpload.length);
          setImageFiles(rollbackFiles);
        }
      } else {
        const errorMessage = res?.error?.message || "LOT 이미지 업로드에 실패했습니다.";
        devError('업로드 실패:', errorMessage);
        toast.error(errorMessage);
        // 업로드 실패 시 추가된 이미지들 제거 (업로드 전 상태로 롤백)
        onInputChange('thumbnailImages', preUploadImageUrls);
        
        const rollbackFiles = imageFiles.slice(0, imageFiles.length - filesToUpload.length);
        setImageFiles(rollbackFiles);
      }
    } catch (error) {
      devError('LOT 여러 이미지 업로드 실패:', error);
      toast.error("LOT 이미지 업로드에 실패했습니다.");
      // 업로드 실패 시 추가된 이미지들 제거 (업로드 전 상태로 롤백)
      onInputChange('thumbnailImages', preUploadImageUrls);
      
      const rollbackFiles = imageFiles.slice(0, imageFiles.length - filesToUpload.length);
      setImageFiles(rollbackFiles);
    } finally {
      setImageUploading(false); // 업로드 완료 후 상태 초기화
    }
  };

  // 단일 이미지 파일 선택 핸들러 (기존 유지, 하나씩 추가할 때 사용)
  const handleImageAdd = async (file: File) => {
    // 여러 이미지 핸들러로 위임
    await handleImagesAdd([file]);
  };

  // 이미지 삭제 핸들러
  const handleImageDelete = (index: number) => {
    const newImages = [...formData.thumbnailImages];
    newImages.splice(index, 1);
    onInputChange('thumbnailImages', newImages);

    const newFiles = [...imageFiles];
    newFiles.splice(index, 1);
    setImageFiles(newFiles);
  };

  // 에디터 내용 변경 핸들러
  const handleEditorChange = (content: string) => {
    onInputChange('artworkDetail', content);
  };

  // 에디터 저장 시 이미지 처리 함수 (NoticePopup과 동일한 로직)
  const processEditorImages = async (editorContent: string): Promise<string> => {
    try {
      // blob URL이 있는지 확인하고 처리
      const blobUrls = editorContent.match(/src="blob:[^"]+"/g) || [];
      let processedContent = editorContent;
      
      if (blobUrls.length > 0) {
        devLog('🖼️ 업로드할 이미지 발견:', blobUrls.length);
        
        // blob URL에서 File 객체들 추출
        const blobFiles: File[] = [];
        
        for (const blobUrlMatch of blobUrls) {
          const blobUrl = blobUrlMatch.replace('src="', '').replace('"', '');
          try {
            const response = await fetch(blobUrl);
            const blob = await response.blob();
            
            let fileExtension = blob.type.split('/')[1] || 'jpg';
            if (fileExtension.includes('+')) {
              fileExtension = fileExtension.split('+')[0];
            }
            
            const fileName = `editor-image-${Date.now()}.${fileExtension}`;
            const file = new File([blob], fileName, { type: blob.type });
            blobFiles.push(file);
          } catch (error) {
            devLog('blob URL 처리 실패:', { blobUrl, error });
          }
        }
        
        // 이미지 업로드 및 콘텐츠 변환
        if (blobFiles.length > 0) {
          const uploadResponse = await uploadEditorImages(blobFiles);
          processedContent = await processEditorContent(editorContent, uploadResponse.data);
        }
      }

      return processedContent;
    } catch (error) {
      devLog('이미지 처리 중 오류:', error);
      toast.error('이미지 처리 중 오류가 발생했습니다.');
      return editorContent; // 오류 발생 시 원본 반환
    }
  };

  // 내부 저장 핸들러 (이미지 처리 후 외부 onSave 호출)
  const handleInternalSave = async () => {
    if (isSaving || isSubmitting || imageUploading) {
      return;
    }

    setIsSubmitting(true);
    try {
      // 에디터 콘텐츠에서 이미지 처리
      const processedArtworkDetail = await processEditorImages(formData.artworkDetail);
      
      // 처리된 데이터로 외부 onSave 호출
      await onSave({
        artworkDetail: processedArtworkDetail,
      });
    } catch (error) {
      devLog('저장 중 오류:', error);
      toast.error('저장 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled = isSaving || isSubmitting || imageUploading;

  const leftButtons = isEditMode && onDelete ? [{
    label: '삭제',
    onClick: onDelete,
    type: CmsPopupBtnType.PRIMARY,
    disabled: isSubmitDisabled,
  }] : [];

  const rightButtons = [
    {
      label: isSubmitDisabled ? '저장 중...' : '저장',
      onClick: handleInternalSave,
      type: CmsPopupBtnType.PRIMARY,
      disabled: isSubmitDisabled,
    },
    {
      label: '닫기',
      onClick: onClose,
      type: CmsPopupBtnType.SECONDARY,
    },
  ];

  return (
    <CmsPopup
      isOpen={isOpen}
      onClose={onClose}
      isWide={true}
      width={'1200px'}
      showRequiredMark={true}
      leftButtons={leftButtons}
      rightButtons={rightButtons}
    >
      <Wrapper>
        <Container>
          <LeftSection>
            <SectionTitleRow>
              <SectionTitle>LOT 등록상세</SectionTitle>
              <RequiredIndicator>*필수항목</RequiredIndicator>
            </SectionTitleRow>
            
            {/* LOT 경매분류 섹션 */}
            <Section>
              <SubSectionTitle>LOT 경매분류</SubSectionTitle>
              
              <FormGroup>
                <AppSearchDropdown
                  label="*경매사 검색"
                  selectedValue={formData.auctioneerName}
                  searchTerm={auctioneerSearchTerm}
                  onSearchTermChange={setAuctioneerSearchTerm}
                  onSearch={handleAuctioneerSearch}
                  onSelectItem={handleAuctioneerSelect}
                  onClear={handleAuctioneerClear}
                  results={auctioneerSearchResults}
                  placeholder={readonlyAuctioneer ? "선택된 경매사" : "경매사를 검색하세요"}
                  loading={auctioneerSearchLoading}
                  errorMessage={formErrors.auctioneerName}
                  emptyMessage="검색된 경매사가 없습니다."
                  autoFocus={false}
                  disabled={readonlyAuctioneer}
                />
              </FormGroup>

              <FormGroup>
                <AppDropdownField
                  label="*대분류"
                  value={formData.majorCategory}
                  onChange={(value) => onInputChange('majorCategory', value)}
                  options={MAJOR_CATEGORY_OPTIONS}
                  placeholder="대분류를 선택하세요"
                  errorMessage={formErrors.majorCategory}
                />
              </FormGroup>

              <FormGroup>
                <AppDropdownField
                  label="*소분류"
                  value={formData.minorCategory}
                  onChange={(value) => onInputChange('minorCategory', value)}
                  options={minorCategoryOptions}
                  placeholder="소분류를 선택하세요"
                  errorMessage={formErrors.minorCategory}
                  disabled={!formData.majorCategory}
                />
              </FormGroup>
            </Section>

            {/* LOT 정보 섹션 */}
            <Section>
              <SubSectionTitle>LOT 정보</SubSectionTitle>
              
              <ImageUploadField>
                <ImageLabel>*썸네일 이미지 등록</ImageLabel>
                <ImageFieldContainer>
                  <ImageScrollContainer>
                    <MultiImageAdd
                      imageUrls={formData.thumbnailImages.map(getImageSrc)}
                      onImageAdd={handleImageAdd}
                      onImagesAdd={handleImagesAdd}
                      onImageDelete={handleImageDelete}
                      maxImages={3}
                      disabled={imageUploading}
                    />
                  </ImageScrollContainer>
                </ImageFieldContainer>
                <ImageNotice>* 최소 1장, 최대 3장의 이미지를 첨부해야 합니다. (현재 {formData.thumbnailImages.length}장)</ImageNotice>
                <FileFormatNotice>WEBP, PNG, JPG, JPEG 파일만 가능</FileFormatNotice>
                {formErrors.thumbnailImages && <ErrorText>{formErrors.thumbnailImages}</ErrorText>}
              </ImageUploadField>
              
              <FormGroup>
                <AppTextField
                  value={formData.artworkName}
                  onChange={(e) => onInputChange('artworkName', e.target.value)}
                  label="*작품명"
                  placeholder="작품명을 입력하세요"
                  errorMessage={formErrors.artworkName}
                />
              </FormGroup>

              <FormGroup>
                <AppNumberTextField
                  label="*시작가 (KRW)"
                  value={formData.startPriceKRW}
                  onChange={(e) => onInputChange('startPriceKRW', parseFloat(e.target.value) || 1000)}
                  placeholder="1000"
                  errorMessage={formErrors.startPriceKRW}
                  minValue={1000}
                  allowDecimal={false}
                  unit="원"
                />
              </FormGroup>
            </Section>
          </LeftSection>

          <RightSection>
            <SectionTitle>*LOT 설명</SectionTitle>
            <EditorContainer>
              <CustomTiptapEditor
              height={947}
              enableImageUpload={false}
                key={`artwork-detail-${formData.auctioneerId}-${isOpen}`}
                content={formData.artworkDetail}
                onChange={handleEditorChange}
                placeholder="작품에 대한 상세 정보를 입력하세요..."
              />
              {formErrors.artworkDetail && <ErrorText>{formErrors.artworkDetail}</ErrorText>}
            </EditorContainer>
          </RightSection>
        </Container>
      </Wrapper>
    </CmsPopup>
  );
};

export default LotFormPopup;

// Styled Components
const Wrapper = styled.div`
  padding-top: 20px;
  height: 100%;
`;

const Container = styled.div`
  display: flex;
  gap: 20px;
  height: 100%;
`;

const LeftSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const RightSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const SectionTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e9ecef;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  text-align: left;
`;

const RequiredIndicator = styled.span`
  font-size: 14px;
  color: #dc3545;
  font-weight: 500;
`;

const Section = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 24px;
  background: #fafafa;
`;

const SubSectionTitle = styled.h4`
  margin: 0 0 20px 0;
  font-size: 16px;
  font-weight: 600;
  color: #495057;
  text-align: left;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ErrorText = styled.span`
  color: #dc3545;
  font-size: 12px;
`;

const ImageUploadField = styled.div`
  position: relative;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 20px;
  background-color: #fafafa;
  margin-bottom: 20px;
`;

const ImageLabel = styled.label`
  position: absolute;
  top: -10px;
  left: 12px;
  background-color: white;
  padding: 0 8px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
`;

const ImageFieldContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 20px 0;
  height: 200px;
  width: 100%;
  overflow: hidden;
`;

const ImageScrollContainer = styled.div`
  width: 100%;
  max-width: 100%;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  display: flex;
  align-items: center;
  white-space: nowrap;
  flex: 1;
  
  /* 내부 컨테이너가 늘어나지 않도록 제한 */
  & > * {
    flex-shrink: 0;
    max-width: none;
  }
  
  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const ImageNotice = styled.div`
  color: #dc3545;
  font-size: 12px;
  text-align: left;
  margin-top: 10px;
`;

const FileFormatNotice = styled.div`
  color: #6c757d;
  font-size: 12px;
  text-align: left;
  margin-top: 5px;
`;

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  
  .ProseMirror {
    min-height: 843px;
    padding: 15px;
    border: 1px solid #ddd;
    border-radius: 4px;
    
    /* &:focus {
      outline: none;
      border-color: #007bff;
      box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
    } */
  }
`;
